/**
 * SidebarContext
 * Manages sidebar visibility state with localStorage persistence
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Platform } from 'react-native';

interface SidebarContextType {
  isVisible: boolean;
  isCollapsed: boolean;
  toggleSidebar: () => void;
  showSidebar: () => void;
  hideSidebar: () => void;
}

const STORAGE_KEY = 'sms_sidebar_visible';

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load saved state from localStorage on mount
  useEffect(() => {
    if (Platform.OS === 'web') {
      try {
        const savedState = localStorage.getItem(STORAGE_KEY);
        if (savedState !== null) {
          setIsVisible(savedState === 'true');
        }
      } catch (error) {
        console.log('Could not load sidebar state from localStorage');
      }
    }
    setIsInitialized(true);
  }, []);

  // Save state to localStorage when it changes
  useEffect(() => {
    if (isInitialized && Platform.OS === 'web') {
      try {
        localStorage.setItem(STORAGE_KEY, String(isVisible));
      } catch (error) {
        console.log('Could not save sidebar state to localStorage');
      }
    }
  }, [isVisible, isInitialized]);

  const toggleSidebar = useCallback(() => {
    setIsVisible(prev => !prev);
  }, []);

  const showSidebar = useCallback(() => {
    setIsVisible(true);
  }, []);

  const hideSidebar = useCallback(() => {
    setIsVisible(false);
  }, []);

  return (
    <SidebarContext.Provider value={{ isVisible, isCollapsed: !isVisible, toggleSidebar, showSidebar, hideSidebar }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarContextType {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

export default SidebarContext;
